import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Crown, Loader2, ShieldCheck, Check } from "lucide-react";
import { load as loadCashfree } from "@cashfreepayments/cashfree-js";
import api, { formatApiErrorDetail, inr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";

let _cfSdkPromise = null;
function ensureCashfree(mode) {
  if (!_cfSdkPromise) _cfSdkPromise = loadCashfree({ mode: mode === "production" ? "production" : "sandbox" });
  return _cfSdkPromise;
}

export default function SubscribeButton({ variant = "inline", onSubscribed }) {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [plan, setPlan] = useState(null);
  const [busy, setBusy] = useState(false);
  const [cfg, setCfg] = useState(null);

  useEffect(() => {
    api.get("/subscriptions/plan").then(({ data }) => setPlan(data)).catch(() => {});
    api.get("/config").then(({ data }) => setCfg(data)).catch(() => {});
  }, []);

  const isActive = !!user?.has_active_subscription;
  const isGrandfathered = !!user?.is_grandfathered;
  const enforcementOff = cfg && !cfg.subscription_enforcement;

  const start = async () => {
    if (!user) return navigate("/login");
    if (user.role !== "tenant") return toast.error("Only tenants can subscribe.");
    setBusy(true);
    try {
      const cf = await ensureCashfree(cfg?.cashfree_env || "sandbox");
      const { data } = await api.post("/subscriptions/create");
      const sub = data.subscription;
      setOpen(false);
      const result = await cf.checkout({
        paymentSessionId: data.payment_session_id,
        redirectTarget: "_modal",
      });
      if (result?.error) {
        toast.error(result.error?.message || "Payment cancelled");
        return;
      }
      try {
        await api.post("/subscriptions/verify", { subscription_id: sub.id, order_id: data.cashfree_order_id });
        toast.success("Subscription active! Owner contacts are now unlocked across LocaNest.");
        await refresh();
        onSubscribed?.();
      } catch (err) {
        toast.info("Payment received — we're confirming with Cashfree. Refresh in a moment.");
      }
    } catch (err) {
      const msg = err?.response ? formatApiErrorDetail(err.response.data?.detail) : (err?.message || "Could not start payment");
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  if (isGrandfathered) {
    return (
      <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[#D4DEC6]/60 text-[#2B3A67] text-xs uppercase tracking-[0.16em]" data-testid="sub-grandfathered-badge">
        <Crown size={13} /> Free forever · early LocaNest member
      </div>
    );
  }

  if (isActive) {
    return (
      <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[#D4DEC6]/60 text-[#2B3A67] text-xs uppercase tracking-[0.16em]" data-testid="sub-active-badge">
        <Crown size={13} /> Access active · expires {user?.subscription_expires_at ? new Date(user.subscription_expires_at).toLocaleDateString() : "—"}
      </div>
    );
  }

  // During the launch/free phase, subscription is intentionally off — don't render a paywall CTA.
  if (enforcementOff) {
    return (
      <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[#F0EBE1] text-[#5C6B7A] text-xs uppercase tracking-[0.16em]" data-testid="sub-launch-badge">
        <Crown size={13} /> Free launch — contacts unlocked for everyone
      </div>
    );
  }

  const cta = variant === "compact" ? (
    <Button onClick={() => setOpen(true)} size="sm" variant="outline" className="rounded-md" data-testid="sub-open-btn-compact">
      <Crown size={13} className="mr-1.5" /> Unlock contacts · {plan ? inr(plan.amount_inr) : "₹99"}/mo
    </Button>
  ) : (
    <Button onClick={() => setOpen(true)} className="w-full mt-3 bg-[#2B3A67] hover:bg-[#1f2c52] text-white rounded-md" data-testid="sub-open-btn">
      <Crown size={15} className="mr-1.5" /> Unlock all owner contacts · {plan ? inr(plan.amount_inr) : "₹99"}/mo
    </Button>
  );

  return (
    <>
      {cta}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>LocaNest Access · {plan ? inr(plan.amount_inr) : "₹99"} / {plan?.days || 30} days</DialogTitle>
            <DialogDescription>
              Unlocks owner contact everywhere for {plan?.days || 30} days. Bookings still finalise on the platform to protect your rating and refund rights.
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-md bg-[#F0EBE1] p-4 space-y-2 text-sm">
            {(plan?.features || [
              "Unlock owner contact on every listing",
              "Unlimited in-app chats",
              "Priority visibility on new inquiries",
            ]).map((f) => (
              <div key={f} className="flex items-center gap-2"><Check size={14} className="text-[#2B3A67]" /> {f}</div>
            ))}
          </div>
          <div className="text-xs text-[#5C6B7A] flex items-center gap-2">
            <ShieldCheck size={13} /> Secured via Cashfree. No auto-renewal — you subscribe again only if you want.
          </div>
          <DialogFooter>
            <Button onClick={start} disabled={busy} className="bg-[#C85A32] hover:bg-[#a8482a] text-white rounded-md" data-testid="sub-pay-btn">
              {busy && <Loader2 size={14} className="mr-1 animate-spin" />}
              Pay {plan ? inr(plan.amount_inr) : "₹99"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
